/**
 * マッチング設定 作成/編集フォーム（Presentation）
 */
import type { FC } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type {
  CreateMatchingSettingInput,
  MatchingSetting,
} from "../../../hooks/use-matching-settings-api";

type MatchingSettingFormPageProps = {
  isEdit: boolean;
  initialData?: MatchingSetting | null;
  loading: boolean;
  onSubmit: (input: CreateMatchingSettingInput) => Promise<void>;
  isSubmitting: boolean;
};

type FormData = {
  name: string;
  description: string;
  matchingMode: string;
  minMargin: string;
  maxEmailAgeDays: string;
  minTotalScorePercent: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

export const MatchingSettingFormPage: FC<MatchingSettingFormPageProps> = ({
  isEdit,
  initialData,
  loading,
  onSubmit,
  isSubmitting,
}) => {
  const [formData, setFormData] = useState<FormData>(() => ({
    name: initialData?.name ?? "",
    description: initialData?.description ?? "",
    matchingMode: initialData?.matchingMode ?? "manual",
    minMargin: initialData ? String(initialData.minMargin) : "50000",
    maxEmailAgeDays:
      initialData?.maxEmailAgeDays != null
        ? String(initialData.maxEmailAgeDays)
        : "",
    minTotalScorePercent: initialData
      ? String(Math.round(initialData.minTotalScore * 100))
      : "50",
  }));

  // 編集モードで initialData がロードされたらフォームを更新
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name,
        description: initialData.description ?? "",
        matchingMode: initialData.matchingMode,
        minMargin: String(initialData.minMargin),
        maxEmailAgeDays:
          initialData.maxEmailAgeDays != null
            ? String(initialData.maxEmailAgeDays)
            : "",
        minTotalScorePercent: String(
          Math.round(initialData.minTotalScore * 100),
        ),
      });
    }
  }, [initialData]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "設定名は必須です";
    } else if (formData.name.length > 255) {
      newErrors.name = "設定名は255文字以内で入力してください";
    }

    const margin = Number(formData.minMargin);
    if (formData.minMargin === "" || Number.isNaN(margin)) {
      newErrors.minMargin = "最低マージンを入力してください";
    } else if (margin < 0) {
      newErrors.minMargin = "0以上の値を入力してください";
    }

    if (formData.maxEmailAgeDays !== "") {
      const days = Number(formData.maxEmailAgeDays);
      if (Number.isNaN(days) || days < 1) {
        newErrors.maxEmailAgeDays = "1以上の値を入力してください";
      }
    }

    const score = Number(formData.minTotalScorePercent);
    if (formData.minTotalScorePercent === "" || Number.isNaN(score)) {
      newErrors.minTotalScorePercent = "最低スコアを入力してください";
    } else if (score < 0 || score > 100) {
      newErrors.minTotalScorePercent = "0〜100の値を入力してください";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitError(null);
    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        matchingMode: formData.matchingMode,
        minMargin: Number(formData.minMargin),
        maxEmailAgeDays:
          formData.maxEmailAgeDays !== ""
            ? Number(formData.maxEmailAgeDays)
            : null,
        minTotalScore: Number(formData.minTotalScorePercent) / 100,
      });
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "保存に失敗しました");
    }
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">
          {isEdit ? "マッチング設定を編集" : "マッチング設定を作成"}
        </h1>
        <p className="text-base-content/60 mt-1">
          マッチング実行時に使用する条件を設定します
        </p>
      </div>

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 設定名 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  設定名 <span className="text-error">*</span>
                </span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => handleChange("name", e.target.value)}
                className={`input input-bordered ${errors.name ? "input-error" : ""}`}
                placeholder="例: デフォルト設定"
              />
              {errors.name && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.name}
                  </span>
                </label>
              )}
            </div>

            {/* 説明 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">説明</span>
              </label>
              <textarea
                value={formData.description}
                onChange={e => handleChange("description", e.target.value)}
                className="textarea textarea-bordered"
                rows={2}
                placeholder="設定の説明（任意）"
              />
            </div>

            <div className="divider my-2" />

            {/* マッチングモード */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  マッチングモード <span className="text-error">*</span>
                </span>
                <span className="label-text-alt">マッチング結果の処理方法</span>
              </label>
              <div className="flex gap-4">
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    className="radio radio-primary"
                    value="manual"
                    checked={formData.matchingMode === "manual"}
                    onChange={e => handleChange("matchingMode", e.target.value)}
                  />
                  <span className="label-text">手動</span>
                </label>
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    className="radio radio-primary"
                    value="review"
                    checked={formData.matchingMode === "review"}
                    onChange={e => handleChange("matchingMode", e.target.value)}
                  />
                  <span className="label-text">レビュー</span>
                </label>
                <label className="label cursor-pointer gap-2">
                  <input
                    type="radio"
                    className="radio radio-primary"
                    value="auto"
                    checked={formData.matchingMode === "auto"}
                    onChange={e => handleChange("matchingMode", e.target.value)}
                  />
                  <span className="label-text">自動</span>
                </label>
              </div>
              <label className="label">
                <span className="label-text-alt text-base-content/50">
                  手動: ユーザーが結果を確認して提案 / レビュー: AI提案を承認 /
                  自動: AI判定で自動提案
                </span>
              </label>
            </div>

            {/* 最低マージン */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  最低マージン（円） <span className="text-error">*</span>
                </span>
                <span className="label-text-alt">BP案件での最低利益額</span>
              </label>
              <input
                type="number"
                value={formData.minMargin}
                onChange={e => handleChange("minMargin", e.target.value)}
                className={`input input-bordered ${errors.minMargin ? "input-error" : ""}`}
                min={0}
                step={10000}
                placeholder="50000"
              />
              {errors.minMargin && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.minMargin}
                  </span>
                </label>
              )}
            </div>

            {/* 最低総合スコア */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">
                  最低総合スコア（%） <span className="text-error">*</span>
                </span>
                <span className="label-text-alt">
                  この値未満の結果は除外されます
                </span>
              </label>
              <input
                type="number"
                value={formData.minTotalScorePercent}
                onChange={e =>
                  handleChange("minTotalScorePercent", e.target.value)
                }
                className={`input input-bordered ${errors.minTotalScorePercent ? "input-error" : ""}`}
                min={0}
                max={100}
                step={5}
                placeholder="50"
              />
              {errors.minTotalScorePercent && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.minTotalScorePercent}
                  </span>
                </label>
              )}
            </div>

            {/* メール有効期間 */}
            <div className="form-control">
              <label className="label">
                <span className="label-text">メール有効期間（日）</span>
                <span className="label-text-alt">空欄 = 制限なし</span>
              </label>
              <input
                type="number"
                value={formData.maxEmailAgeDays}
                onChange={e => handleChange("maxEmailAgeDays", e.target.value)}
                className={`input input-bordered ${errors.maxEmailAgeDays ? "input-error" : ""}`}
                min={1}
                placeholder="制限なし"
              />
              {errors.maxEmailAgeDays && (
                <label className="label">
                  <span className="label-text-alt text-error">
                    {errors.maxEmailAgeDays}
                  </span>
                </label>
              )}
            </div>

            {submitError && (
              <div className="alert alert-error">
                <span>{submitError}</span>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Link
                to="/settings/matching/conditions"
                className="btn btn-ghost"
              >
                キャンセル
              </Link>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : isEdit ? (
                  "更新"
                ) : (
                  "作成"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
